package com.daytimegaming.ourdailyfinances.presentation.account

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.LazyColumn
import androidx.compose.foundation.lazy.items
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.automirrored.filled.ArrowBack
import androidx.compose.material.icons.filled.Settings
import androidx.compose.material.icons.filled.TrendingFlat
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.ExperimentalMaterial3Api
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Scaffold
import androidx.compose.material3.Surface
import androidx.compose.material3.Text
import androidx.compose.material3.TopAppBar
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.tooling.preview.PreviewLightDark
import androidx.compose.ui.tooling.preview.PreviewParameter
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import androidx.lifecycle.compose.collectAsStateWithLifecycle
import com.daytimegaming.ourdailyfinances.domain.model.RealEstateValueEstimate
import com.daytimegaming.ourdailyfinances.presentation.preview.ViewRealEstateHoldingPreviewParameterProvider
import com.daytimegaming.ourdailyfinances.presentation.theme.AppTheme
import com.daytimegaming.ourdailyfinances.presentation.theme.GlassCard
import com.daytimegaming.ourdailyfinances.presentation.util.formatAmount
import org.koin.compose.viewmodel.koinViewModel
import org.koin.core.parameter.parametersOf
import kotlin.random.Random

@Composable
fun ViewRealEstateHoldingScreen(
    realEstateHoldingId: String,
    onNavigateBack: () -> Unit,
    onConfigureClick: (String) -> Unit,
    onInvalidAccess: (() -> Unit)? = null,
    viewModel: ViewRealEstateHoldingViewModel = koinViewModel(key = remember { Random.nextInt().toString() }) {
        parametersOf(realEstateHoldingId)
    }
) {
    val state by viewModel.state.collectAsStateWithLifecycle()

    LaunchedEffect(state) {
        if (state is ViewRealEstateHoldingScreenState.Error && onInvalidAccess != null) {
            onInvalidAccess()
        }
    }

    ViewRealEstateHoldingScreenContent(
        state = state,
        onNavigateBack = onNavigateBack,
        onConfigureClick = onConfigureClick
    )
}

@OptIn(ExperimentalMaterial3Api::class)
@Composable
fun ViewRealEstateHoldingScreenContent(
    state: ViewRealEstateHoldingScreenState,
    onNavigateBack: () -> Unit,
    onConfigureClick: (String) -> Unit
) {
    val title = when (state) {
        is ViewRealEstateHoldingScreenState.Loaded -> state.holding.nickname
        else -> "Real Estate"
    }

    Scaffold(
        topBar = {
            TopAppBar(
                title = { Text(title) },
                navigationIcon = {
                    IconButton(onClick = onNavigateBack) {
                        Icon(
                            imageVector = Icons.AutoMirrored.Filled.ArrowBack,
                            contentDescription = "Back"
                        )
                    }
                },
                actions = {
                    if (state is ViewRealEstateHoldingScreenState.Loaded && state.isOwner) {
                        IconButton(onClick = { onConfigureClick(state.holding.realEstateHoldingId) }) {
                            Icon(
                                imageVector = Icons.Default.Settings,
                                contentDescription = "Configure Real Estate"
                            )
                        }
                    }
                }
            )
        }
    ) { innerPadding ->
        when (state) {
            is ViewRealEstateHoldingScreenState.Loading -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentAlignment = Alignment.Center
                ) {
                    CircularProgressIndicator(color = MaterialTheme.colorScheme.primary)
                }
            }
            is ViewRealEstateHoldingScreenState.Error -> {
                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding)
                        .padding(16.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = state.message,
                        color = MaterialTheme.colorScheme.error,
                        style = MaterialTheme.typography.bodyMedium
                    )
                }
            }
            is ViewRealEstateHoldingScreenState.Loaded -> {
                val holding = state.holding
                val latestEstimate = holding.valueEstimates.maxByOrNull { it.date }
                val currentPrice = latestEstimate?.estimatedValue ?: holding.purchasePrice

                LazyColumn(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(innerPadding),
                    contentPadding = PaddingValues(horizontal = 16.dp, vertical = 8.dp),
                    verticalArrangement = Arrangement.spacedBy(16.dp)
                ) {
                    // Valuation Hero Section
                    item {
                        Column(
                            modifier = Modifier
                                .fillMaxWidth()
                                .padding(vertical = 12.dp),
                            horizontalAlignment = Alignment.CenterHorizontally
                        ) {
                            Text(
                                text = "Current Estimated Price",
                                style = MaterialTheme.typography.labelSmall.copy(letterSpacing = 2.sp),
                                color = MaterialTheme.colorScheme.onSurfaceVariant,
                                fontWeight = FontWeight.Bold
                            )
                            Spacer(modifier = Modifier.height(4.dp))
                            Text(
                                text = "$${currentPrice.formatAmount()}",
                                style = MaterialTheme.typography.displayLarge.copy(fontSize = 40.sp),
                                color = MaterialTheme.colorScheme.onSurface,
                                fontWeight = FontWeight.Bold
                            )
                            latestEstimate?.let {
                                Spacer(modifier = Modifier.height(4.dp))
                                Text(
                                    text = "Estimated range: $${it.priceRangeLow?.formatAmount() ?: "N/A"} - $${it.priceRangeHigh?.formatAmount() ?: "N/A"}",
                                    style = MaterialTheme.typography.bodySmall,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    }

                    // Net Equity & Financials Bento Card
                    item {
                        GlassCard(modifier = Modifier.fillMaxWidth()) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Text(
                                    text = "Financial Position",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                                FinancialRow("Net Equity (Value - Mortgage)", "$${holding.netValue.formatAmount()}", isHighlight = true)
                                FinancialRow("Purchase Price", "$${holding.purchasePrice.formatAmount()}")
                                holding.mortgageAmount?.let {
                                    FinancialRow("Mortgage Principal", "$${it.formatAmount()}")
                                }
                                holding.mortgageInterestRate?.let {
                                    FinancialRow("Interest Rate", "$it%")
                                }
                            }
                        }
                    }

                    // Address Details Card
                    item {
                        GlassCard(modifier = Modifier.fillMaxWidth()) {
                            Column(
                                verticalArrangement = Arrangement.spacedBy(12.dp)
                            ) {
                                Text(
                                    text = "Location Details",
                                    style = MaterialTheme.typography.titleMedium,
                                    fontWeight = FontWeight.Bold,
                                    color = MaterialTheme.colorScheme.primary
                                )
                                Text(
                                    text = buildString {
                                        append(holding.addressLine1)
                                        if (!holding.addressLine2.isNullOrEmpty()) {
                                            append(", ").append(holding.addressLine2)
                                        }
                                        append("\n").append(holding.city).append(", ").append(holding.state).append(" ").append(holding.zipCode)
                                        if (!holding.county.isNullOrEmpty()) {
                                            append("\n").append(holding.county).append(" County")
                                        }
                                    },
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurface,
                                    lineHeight = 22.sp
                                )
                            }
                        }
                    }

                    // Estimates History List
                    item {
                        Text(
                            text = "Historical Valuations",
                            style = MaterialTheme.typography.titleMedium,
                            fontWeight = FontWeight.Bold,
                            color = MaterialTheme.colorScheme.onSurface,
                            modifier = Modifier.padding(top = 8.dp)
                        )
                    }

                    if (holding.valueEstimates.isEmpty()) {
                        item {
                            GlassCard(modifier = Modifier.fillMaxWidth()) {
                                Text(
                                    text = "No estimates fetched yet. Value estimates automatically refresh every 30 days.",
                                    style = MaterialTheme.typography.bodyMedium,
                                    color = MaterialTheme.colorScheme.onSurfaceVariant
                                )
                            }
                        }
                    } else {
                        items(holding.valueEstimates.sortedByDescending { it.date }) { estimate ->
                            EstimateRowItem(estimate = estimate)
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun FinancialRow(
    label: String,
    value: String,
    isHighlight: Boolean = false
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        horizontalArrangement = Arrangement.SpaceBetween,
        verticalAlignment = Alignment.CenterVertically
    ) {
        Text(
            text = label,
            style = if (isHighlight) MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold) else MaterialTheme.typography.bodyMedium,
            color = if (isHighlight) MaterialTheme.colorScheme.onSurface else MaterialTheme.colorScheme.onSurfaceVariant
        )
        Text(
            text = value,
            style = if (isHighlight) MaterialTheme.typography.titleMedium.copy(fontWeight = FontWeight.Bold) else MaterialTheme.typography.bodyMedium.copy(fontWeight = FontWeight.Bold),
            color = if (isHighlight) MaterialTheme.colorScheme.primary else MaterialTheme.colorScheme.onSurface
        )
    }
}

@Composable
private fun EstimateRowItem(estimate: RealEstateValueEstimate) {
    Row(
        modifier = Modifier
            .fillMaxWidth()
            .clip(RoundedCornerShape(8.dp))
            .background(Color(0x06FFFFFF))
            .border(1.dp, Color(0x0AFFFFFF), RoundedCornerShape(8.dp))
            .padding(16.dp),
        verticalAlignment = Alignment.CenterVertically
    ) {
        Box(
            modifier = Modifier
                .size(40.dp)
                .background(MaterialTheme.colorScheme.surfaceContainerHighest, CircleShape),
            contentAlignment = Alignment.Center
        ) {
            Icon(
                imageVector = Icons.Default.TrendingFlat,
                contentDescription = null,
                tint = MaterialTheme.colorScheme.primary,
                modifier = Modifier.size(20.dp)
            )
        }
        Spacer(modifier = Modifier.width(12.dp))
        Column(modifier = Modifier.weight(1f)) {
            Text(
                text = "Estimated Value: $${estimate.estimatedValue.formatAmount()}",
                style = MaterialTheme.typography.bodyMedium,
                fontWeight = FontWeight.Bold,
                color = MaterialTheme.colorScheme.onSurface
            )
            Spacer(modifier = Modifier.height(2.dp))
            Text(
                text = "Range: $${estimate.priceRangeLow?.formatAmount() ?: "N/A"} - $${estimate.priceRangeHigh?.formatAmount() ?: "N/A"}",
                style = MaterialTheme.typography.labelSmall,
                color = MaterialTheme.colorScheme.onSurfaceVariant
            )
        }
        Spacer(modifier = Modifier.width(8.dp))
        Text(
            text = estimate.date,
            style = MaterialTheme.typography.labelSmall,
            color = MaterialTheme.colorScheme.onSurfaceVariant.copy(alpha = 0.7f)
        )
    }
}

@PreviewLightDark
@Composable
fun ViewRealEstateHoldingScreenPreview(
    @PreviewParameter(ViewRealEstateHoldingPreviewParameterProvider::class) state: ViewRealEstateHoldingScreenState
) {
    AppTheme(darkTheme = isSystemInDarkTheme(), dynamicColor = false) {
        Surface {
            ViewRealEstateHoldingScreenContent(
                state = state,
                onNavigateBack = {},
                onConfigureClick = {}
            )
        }
    }
}
